/**
 * GARCHComparisonTab - Advanced GARCH vs Standard Simulation Comparison Component
 *
 * This educational component provides side-by-side comparison between GARCH-enhanced
 * and standard simulation methods, demonstrating the impact of advanced volatility
 * modeling on portfolio strategy performance.
 *
 * Key Features:
 * - Side-by-side GARCH vs Standard comparison
 * - Educational content about volatility modeling benefits
 * - Detailed statistical analysis of modeling differences
 * - Interactive parameter adjustment and real-time comparison
 * - Export functionality for comparative analysis results
 *
 * @author Portfolio-3Topf Team
 * @version 1.0.0
 * @since 2.1.0
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { ThreeTopfSimulationOptions as SimulationOptions, ThreeTopfYearData as YearData } from '@/types/simulation';
import { OneTopfSimulationOptions, OneTopfYearData, SteuerParameter } from '@/types';
import { GARCHRateCalculationIntegration } from '@/utils/garchModeling';

/**
 * Props für die GARCHComparisonTab-Komponente
 */
interface GARCHComparisonTabProps {
  /** Standard 3-Topf simulation data (non-GARCH) */
  standardSimulationData: YearData[];

  /** GARCH-enhanced 3-Topf simulation data */
  garchSimulationData: YearData[];

  /** Standard 1-Topf simulation data (non-GARCH) */
  standardOneTopfData: OneTopfYearData[];

  /** GARCH-enhanced 1-Topf simulation data */
  garchOneTopfData: OneTopfYearData[];

  /** Current tax parameters */
  steuerParameter: SteuerParameter;

  /** Standard simulation options */
  standardOptions: SimulationOptions;

  /** GARCH simulation options */
  garchOptions: SimulationOptions;

  /** Standard 1-Topf options */
  standardOneTopfOptions: OneTopfSimulationOptions;

  /** GARCH 1-Topf options */
  garchOneTopfOptions: OneTopfSimulationOptions;

  /** Callback to trigger standard simulation */
  onRunStandardSimulation: () => void;

  /** Callback to trigger GARCH simulation */
  onRunGARCHSimulation: () => void;

  /** Loading states */
  isRunningStandard: boolean;
  isRunningGARCH: boolean;
}

/**
 * GARCHComparisonTab - Main component for GARCH vs Standard analysis
 */
export const GARCHComparisonTab: React.FC<GARCHComparisonTabProps> = ({
  standardSimulationData,
  garchSimulationData,
  standardOneTopfData,
  garchOneTopfData,
  steuerParameter: _steuerParameter,
  standardOptions: _standardOptions,
  garchOptions: _garchOptions,
  standardOneTopfOptions: _standardOneTopfOptions,
  garchOneTopfOptions: _garchOneTopfOptions,
  onRunStandardSimulation: _onRunStandardSimulation,
  onRunGARCHSimulation: _onRunGARCHSimulation,
  isRunningStandard: _isRunningStandard,
  isRunningGARCH: _isRunningGARCH,
}) => {
  const [showDetailedAnalysis] = useState(false);
  const selectedStrategy = 'threeTopf'; // Default to 3-Topf strategy

  // GARCH analysis data (needed for export functionality)
  const garchAnalysis = useMemo(() => {
    try {
      return GARCHRateCalculationIntegration.analyzeIndexDataForGARCH('world');
    } catch (error) {
      // Failed to load GARCH analysis
      return null;
    }
  }, []);

  // Calculate comparison statistics
  const comparisonStats = useMemo(() => {
    const selectedStandardData =
      selectedStrategy === 'threeTopf' ? standardSimulationData : standardOneTopfData;
    const selectedGARCHData =
      selectedStrategy === 'threeTopf' ? garchSimulationData : garchOneTopfData;

    if (!selectedStandardData.length || !selectedGARCHData.length) {
      return null;
    }

    const calculateStats = (data: (YearData | OneTopfYearData)[]) => {
      const finalBalance = data[data.length - 1]?.gesamtsaldo || 0;
      const totalWithdrawals = data.reduce((sum, year) => sum + year.gesamtentnahme, 0);
      const yearsFunded = data.length - 1;

      // Calculate volatility of returns
      const returns = data.slice(1).map((year, index) => {
        const prevYear = data[index];
        if ('investitionstopfRendite' in year && 'investitionstopfRendite' in prevYear) {
          return year.investitionstopfRendite;
        }
        return 0;
      });

      const meanReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
      const returnVolatility = Math.sqrt(
        returns.reduce((sum, r) => sum + Math.pow(r - meanReturn, 2), 0) / returns.length
      );

      // Count crisis periods
      const crisisPeriods = data.filter(year => year.istKrise).length;

      return {
        finalBalance,
        totalWithdrawals,
        yearsFunded,
        meanReturn,
        returnVolatility,
        crisisPeriods,
        successRate: finalBalance > 0 ? 100 : 0,
      };
    };

    const standardStats = calculateStats(selectedStandardData);
    const garchStats = calculateStats(selectedGARCHData);

    return {
      standard: standardStats,
      garch: garchStats,
      improvements: {
        finalBalanceDiff: garchStats.finalBalance - standardStats.finalBalance,
        volatilityReduction: standardStats.returnVolatility - garchStats.returnVolatility,
        riskAdjustedReturn:
          garchStats.meanReturn / garchStats.returnVolatility -
          standardStats.meanReturn / standardStats.returnVolatility,
      },
    };
  }, [
    selectedStrategy,
    standardSimulationData,
    garchSimulationData,
    standardOneTopfData,
    garchOneTopfData,
  ]);

  // Comparison table columns
  const comparisonTableColumns = [
    {
      id: 'metric',
      header: 'Metrik',
      cell: (item: any) => item.metric,
      width: 200,
    },
    {
      id: 'standard',
      header: 'Standard Modell',
      cell: (item: any) => item.standard,
      width: 150,
    },
    {
      id: 'garch',
      header: 'GARCH Modell',
      cell: (item: any) => item.garch,
      width: 150,
    },
    {
      id: 'improvement',
      header: 'Verbesserung',
      cell: (item: any) => <Badge color={item.improvementColor}>{item.improvement}</Badge>,
      width: 120,
    },
  ];

  // Generate comparison data
  const comparisonTableData = useMemo(() => {
    if (!comparisonStats) {return [];}

    return [
      {
        metric: 'Endsaldo',
        standard: `${comparisonStats.standard.finalBalance.toLocaleString('de-DE')} €`,
        garch: `${comparisonStats.garch.finalBalance.toLocaleString('de-DE')} €`,
        improvement:
          comparisonStats.improvements.finalBalanceDiff >= 0
            ? `+${comparisonStats.improvements.finalBalanceDiff.toLocaleString('de-DE')} €`
            : `${comparisonStats.improvements.finalBalanceDiff.toLocaleString('de-DE')} €`,
        improvementColor: comparisonStats.improvements.finalBalanceDiff >= 0 ? 'green' : 'red',
      },
      {
        metric: 'Durchschnittsrendite',
        standard: `${comparisonStats.standard.meanReturn.toFixed(2)}%`,
        garch: `${comparisonStats.garch.meanReturn.toFixed(2)}%`,
        improvement: `${(comparisonStats.garch.meanReturn - comparisonStats.standard.meanReturn).toFixed(2)}%`,
        improvementColor:
          comparisonStats.garch.meanReturn >= comparisonStats.standard.meanReturn ? 'green' : 'red',
      },
      {
        metric: 'Volatilität',
        standard: `${comparisonStats.standard.returnVolatility.toFixed(2)}%`,
        garch: `${comparisonStats.garch.returnVolatility.toFixed(2)}%`,
        improvement:
          comparisonStats.improvements.volatilityReduction >= 0
            ? `-${comparisonStats.improvements.volatilityReduction.toFixed(2)}%`
            : `+${Math.abs(comparisonStats.improvements.volatilityReduction).toFixed(2)}%`,
        improvementColor: comparisonStats.improvements.volatilityReduction >= 0 ? 'green' : 'red',
      },
      {
        metric: 'Krisen-Perioden',
        standard: `${comparisonStats.standard.crisisPeriods}`,
        garch: `${comparisonStats.garch.crisisPeriods}`,
        improvement: `${comparisonStats.garch.crisisPeriods - comparisonStats.standard.crisisPeriods}`,
        improvementColor:
          comparisonStats.garch.crisisPeriods <= comparisonStats.standard.crisisPeriods
            ? 'green'
            : 'blue',
      },
      {
        metric: 'Risiko-Rendite Verhältnis',
        standard: `${(comparisonStats.standard.meanReturn / comparisonStats.standard.returnVolatility).toFixed(3)}`,
        garch: `${(comparisonStats.garch.meanReturn / comparisonStats.garch.returnVolatility).toFixed(3)}`,
        improvement: `${comparisonStats.improvements.riskAdjustedReturn >= 0 ? '+' : ''}${comparisonStats.improvements.riskAdjustedReturn.toFixed(3)}`,
        improvementColor: comparisonStats.improvements.riskAdjustedReturn >= 0 ? 'green' : 'red',
      },
    ];
  }, [comparisonStats]);

  // Export comparison results
  const handleExportComparison = () => {
    if (!comparisonStats || !garchAnalysis) {return;}

    const exportData = {
      exportInfo: {
        title: 'GARCH vs Standard Simulation Comparison',
        exportDate: new Date().toISOString(),
        strategy: selectedStrategy,
        comparisonType: 'Detailed GARCH Modeling Analysis',
      },
      garchModelParameters: {
        indexType: garchAnalysis.indexType,
        modelType: 'EGARCH(1,1)',
        dataPoints: garchAnalysis.dataPoints,
        calibratedParameters: {
          volatilityPersistence: garchAnalysis.garchParameters.volatilityPersistence,
          archEffect: garchAnalysis.garchParameters.archEffect,
          leverageEffect: garchAnalysis.garchParameters.leverageEffect,
        },
        historicalStatistics: {
          mean: garchAnalysis.historicalMean,
          volatility: garchAnalysis.historicalVolatility,
          skewness: garchAnalysis.skewness,
          kurtosis: garchAnalysis.kurtosis,
        },
      },
      comparisonResults: {
        standardModel: comparisonStats.standard,
        garchModel: comparisonStats.garch,
        improvements: comparisonStats.improvements,
      },
      detailedAnalysis: {
        standardData:
          selectedStrategy === 'threeTopf' ? standardSimulationData : standardOneTopfData,
        garchData: selectedStrategy === 'threeTopf' ? garchSimulationData : garchOneTopfData,
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `garch-comparison-${selectedStrategy}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const hasData =
    (standardSimulationData.length > 0 || standardOneTopfData.length > 0) &&
    (garchSimulationData.length > 0 || garchOneTopfData.length > 0);

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Comparison Results */}
      {hasData && comparisonStats && (
        <>
          <Container
            header={
              <Header variant="h3">
                Vergleichsergebnisse - {selectedStrategy === 'threeTopf' ? '3-Topf' : '1-Topf'}{' '}
                Strategie
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="m">
              <Table
                columnDefinitions={comparisonTableColumns}
                items={comparisonTableData}
                loadingText="Berechne Vergleich..."
                empty={
                  <Box textAlign="center" color="inherit">
                    <p>Keine Vergleichsdaten verfügbar</p>
                  </Box>
                }
              />

              <Box textAlign="right">
                <Button onClick={handleExportComparison} iconName="download">
                  Vergleich exportieren (JSON)
                </Button>
              </Box>
            </SpaceBetween>
          </Container>

          {/* Educational Analysis */}
          {showDetailedAnalysis && (
            <Container
              header={<Header variant="h3">Detaillierte GARCH-Modellierung Analyse</Header>}
            >
              <SpaceBetween direction="vertical" size="m">
                <Alert type="success" statusIconAriaLabel="Analyse">
                  <strong>Warum GARCH-Modellierung wichtig ist:</strong> Traditionelle Finanzmodelle
                  nehmen oft konstante Volatilität an, was unrealistisch ist. GARCH-Modelle erfassen
                  die Realität finanzieller Märkte besser durch zeitvariable Volatilität und
                  Krisen-Clustering.
                </Alert>

                <ColumnLayout columns={2} borders="vertical">
                  <Box>
                    <Header variant="h3">Standard Volatilitäts-Modell</Header>
                    <SpaceBetween direction="vertical" size="xs">
                      <p>
                        <strong>Annahmen:</strong>
                      </p>
                      <ul>
                        <li>Konstante Volatilität über Zeit</li>
                        <li>Unabhängige Renditen</li>
                        <li>Symmetrische Marktreaktionen</li>
                        <li>Normalverteilte Renditen</li>
                      </ul>
                      <p>
                        <strong>Limitierungen:</strong> Unterschätzt Krisen-Risiken und
                        Volatilitäts-Clustering
                      </p>
                    </SpaceBetween>
                  </Box>

                  <Box>
                    <Header variant="h3">GARCH Volatilitäts-Modell</Header>
                    <SpaceBetween direction="vertical" size="xs">
                      <p>
                        <strong>Realistische Features:</strong>
                      </p>
                      <ul>
                        <li>Zeitvariable Volatilität</li>
                        <li>Volatilitäts-Clustering (Krisen-Perioden)</li>
                        <li>Leverage-Effekte (asymmetrische Reaktionen)</li>
                        <li>Mean Reversion der Volatilität</li>
                      </ul>
                      <p>
                        <strong>Vorteile:</strong> Bessere Risiko-Schätzung und realistische
                        Krisen-Simulation
                      </p>
                    </SpaceBetween>
                  </Box>
                </ColumnLayout>

                {comparisonStats.improvements.riskAdjustedReturn > 0 && (
                  <Alert type="success" statusIconAriaLabel="Verbesserung">
                    <strong>GARCH Modellierung zeigt Verbesserungen:</strong> Das risiko-adjustierte
                    Rendite-Verhältnis ist um{' '}
                    {comparisonStats.improvements.riskAdjustedReturn.toFixed(3)}
                    Punkte höher, was auf eine effizientere Risiko-Rendite Balance hinweist.
                  </Alert>
                )}
              </SpaceBetween>
            </Container>
          )}
        </>
      )}
    </SpaceBetween>
  );
};
